import { Suspense, lazy } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { createLoginRedirectState, getLoginReturnPath } from './lib/auth-redirect';
import { authSession } from './lib/auth-session';

const App = lazy(async () => {
  const module = await import('./App');
  return { default: module.App };
});

const LoginPage = lazy(async () => {
  const module = await import('./pages/login-page');
  return { default: module.LoginPage };
});

const AdminUsersPage = lazy(async () => {
  const module = await import('./pages/admin-users-page');
  return { default: module.AdminUsersPage };
});

const AdminSystemPage = lazy(async () => {
  const module = await import('./pages/admin-system-page');
  return { default: module.AdminSystemPage };
});

const ChangelogPage = lazy(async () => {
  const module = await import('./pages/changelog-page');
  return { default: module.ChangelogPage };
});

function RouteFallback() {
  return <div className="bg-background min-h-svh" aria-hidden="true" />;
}

function hasAuthSession() {
  return authSession.hasSession();
}

function ProtectedAppRoute() {
  const location = useLocation();

  if (!hasAuthSession()) {
    return <Navigate to="/login" replace state={createLoginRedirectState(location)} />;
  }

  return (
    <Suspense fallback={<RouteFallback />}>
      <App />
    </Suspense>
  );
}

function ProtectedAdminUsersRoute() {
  const location = useLocation();

  if (!hasAuthSession()) {
    return <Navigate to="/login" replace state={createLoginRedirectState(location)} />;
  }

  return (
    <Suspense fallback={<RouteFallback />}>
      <AdminUsersPage />
    </Suspense>
  );
}

function ProtectedAdminSystemRoute() {
  const location = useLocation();

  if (!hasAuthSession()) {
    return <Navigate to="/login" replace state={createLoginRedirectState(location)} />;
  }

  return (
    <Suspense fallback={<RouteFallback />}>
      <AdminSystemPage />
    </Suspense>
  );
}

function ProtectedChangelogRoute() {
  const location = useLocation();

  if (!hasAuthSession()) {
    return <Navigate to="/login" replace state={createLoginRedirectState(location)} />;
  }

  return (
    <Suspense fallback={<RouteFallback />}>
      <ChangelogPage />
    </Suspense>
  );
}

function PublicLoginRoute() {
  const location = useLocation();

  if (hasAuthSession()) {
    return <Navigate to={getLoginReturnPath(location.state)} replace />;
  }

  return (
    <Suspense fallback={<RouteFallback />}>
      <LoginPage />
    </Suspense>
  );
}

export function AppRouter() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<PublicLoginRoute />} />
        <Route path="/" element={<ProtectedAppRoute />} />
        <Route path="/admin/users" element={<ProtectedAdminUsersRoute />} />
        <Route path="/admin/system" element={<ProtectedAdminSystemRoute />} />
        <Route path="/changelog" element={<ProtectedChangelogRoute />} />
        <Route path="*" element={<Navigate to={hasAuthSession() ? '/' : '/login'} replace />} />
      </Routes>
    </BrowserRouter>
  );
}
